import React, { useRef, useEffect } from 'react';
import styled from 'styled-components';
import InfoIcon from '../../Images/info-outline.svg';
import ConversationIcon from '../../Images/chat-conversation.svg';

const DialogOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(23, 36, 62, 0.35);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  backdrop-filter: blur(3px);
`;

const DialogContainer = styled.div`
  width: 90%;
  max-width: 750px;
  max-height: 90vh;
  background: linear-gradient(180deg, #f9fbff 0%, #f5f8fd 100%);
  border-radius: 10px;
  box-shadow: 0 8px 30px rgba(39, 68, 131, 0.105);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  border: 1px solid #edf1f9;
  
  @media (max-width: 768px) {
    width: 95%;
    max-height: 95vh;
  }
`;

const DialogHeader = styled.div`
  padding: 20px;
  background: linear-gradient(135deg, #ffffff 0%, #f8faff 100%);
  border-bottom: 1px solid #e0e8f5;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const DialogTitle = styled.h2`
  margin: 0;
  color: #192947;
  font-size: 18px;
  font-weight: 500;
  font-family: 'Inter', 'Segoe UI', -apple-system, BlinkMacSystemFont, Arial, sans-serif;
  
  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: #455776;
  font-size: 20px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 4px;
  transition: all 0.2s;
  
  &:hover {
    background-color: #e6ecf6;
    color: #192947;
  }
`;

const DialogContent = styled.div`
  padding: 5px 20px 20px;
  overflow-y: auto;
  color: #192947;
  font-size: 14px;
  line-height: 1.5;
  
  /* Scrollbar styling */
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(25, 41, 71, 0.025);
  }
  
  &::-webkit-scrollbar-thumb {
    background-color: #e6ecf6;
    border-radius: 4px;
  }
`;

const SectionTitle = styled.h3`
  margin: 20px 0 10px;
  color: #192947;
  font-size: 16px;
  font-weight: 500;
  border-bottom: 1px solid #e0e8f5;
  padding-bottom: 8px;
  
  &:first-child {
    margin-top: 15px;
  }
`;

const Paragraph = styled.p`
  margin: 10px 0;
  color: #33445e;
  font-size: 14px;
  line-height: 1.6;
`;

const List = styled.ul`
  margin: 10px 0;
  padding-left: 20px;
  
  li {
    margin-bottom: 8px;
    color: #33445e;
  }
`;

const FeatureImage = styled.div`
  margin: 15px 0;
  padding: 10px;
  border-radius: 8px;
  background-color: #f5f8fd;
  width: 100%;
  max-width: 100%;
  
  img {
    width: 100%;
    border-radius: 4px;
    border: 1px solid #e0e8f5;
  }
  
  figcaption {
    margin-top: 8px;
    text-align: center;
    font-size: 12px;
    color: #65748d;
  }
`;

const FeatureBox = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 15px;
  padding: 15px;
  background-color: #f8faff;
  border-radius: 8px;
  margin: 15px 0;
`;

const IconBox = styled.div`
  width: 36px;
  height: 36px;
  background-color: #e6ecf6;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  
  img {
    width: 20px;
    height: 20px;
    filter: brightness(0) saturate(100%) invert(34%) sepia(15%) saturate(1133%) hue-rotate(179deg) brightness(88%) contrast(88%);
  }
`;

const FeatureContent = styled.div`
  flex: 1;
  
  h4 {
    margin: 0 0 8px 0;
    font-size: 15px;
    font-weight: 500;
    color: #192947;
  }
  
  p {
    margin: 0;
    font-size: 13px;
    color: #33445e;
    line-height: 1.5;
  }
`;

const Footer = styled.div`
  padding: 15px 20px;
  background: linear-gradient(135deg, #ffffff 0%, #f8faff 100%);
  border-top: 1px solid #e0e8f5;
  display: flex;
  justify-content: flex-end;
`;

const ActionButton = styled.button`
  padding: 8px 16px;
  border-radius: 6px;
  background-color: #335cf4;
  color: white;
  border: none;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background-color: #3a62c4;
  }
`;

const UserEntryExample = styled.div`
  background-color: #ffffff;
  border: 1px solid #edf1f9;
  border-radius: 8px;
  padding: 15px;
  margin: 15px 0;
`;

const QuickActionExample = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin: 15px 0;
  
  .action-button {
    width: 28px;
    height: 28px;
    border-radius: 4px;
    background-color: #f5f8fd;
    display: flex;
    align-items: center;
    justify-content: center;
    
    img {
      width: 16px;
      height: 16px;
      filter: brightness(0) saturate(100%) invert(34%) sepia(15%) saturate(1133%) hue-rotate(179deg) brightness(88%) contrast(88%);
    }
  }
`;

const ContextMenuExample = styled.div`
  background: linear-gradient(135deg, #ffffff 0%, #f8faff 100%);
  border: 1px solid #e0e8f5;
  border-radius: 8px;
  width: 150px;
  margin: 15px auto;
  overflow: hidden;
  
  .menu-item {
    padding: 8px 12px;
    font-size: 13px;
    color: #192947;
    cursor: pointer;
    
    &:hover {
      background-color: rgba(51, 92, 244, 0.1);
    }
  }
`;

const HelpDialog = ({ title, helpType, onClose }) => {
  const dialogRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dialogRef.current && !dialogRef.current.contains(event.target)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose]);

  // Render different help content based on the helpType
  const renderHelpContent = () => {
    switch (helpType) {
      case 'userList':
        return (
          <>
            <SectionTitle>User List Overview</SectionTitle>
            <Paragraph>
              The User List displays all advisers that you have started conversations with. Each entry shows an adviser with their profile 
              image, name, and the date and time of your conversation. Clicking on any adviser will open your conversation in the main
              view.
            </Paragraph>
            
            <UserEntryExample>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{ 
                    width: '35px', 
                    height: '35px', 
                    background: 'linear-gradient(135deg, #f8faff 0%, #edf2ff 100%)', 
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#192947',
                    fontSize: '16px'
                  }}>
                    A
                  </div>
                  <div>
                    <div style={{ fontSize: '14px', color: '#192947' }}>Adviser Name</div>
                    <div style={{ fontSize: '12px', color: '#65748d' }}>May 19, 12:09 PM</div>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <div style={{ 
                    width: '8px', 
                    height: '8px', 
                    backgroundColor: '#1f9d6b', 
                    borderRadius: '50%' 
                  }}></div>
                  <div style={{ 
                    display: 'flex',
                    flexDirection: 'column',
                    height: '12px',
                    justifyContent: 'space-between',
                    padding: '0 4px'
                  }}>
                    <div style={{ width: '3px', height: '3px', borderRadius: '50%', backgroundColor: '#dce5f4' }}></div>
                    <div style={{ width: '3px', height: '3px', borderRadius: '50%', backgroundColor: '#dce5f4' }}></div>
                    <div style={{ width: '3px', height: '3px', borderRadius: '50%', backgroundColor: '#dce5f4' }}></div>
                  </div>
                </div>
              </div>
            </UserEntryExample>
            <figcaption style={{ textAlign: 'center', color: '#65748d', fontSize: '12px', marginTop: '5px' }}>
              Adviser entry with profile image, name, date, online status indicator, and three dots menu
            </figcaption>
            
            <SectionTitle>Search and Filtering</SectionTitle>
            <Paragraph>
              The search bar at the top allows you to quickly find advisers by name. The filter button next to it provides additional filtering options:
            </Paragraph>
            
            <List>
              <li><strong>Status</strong> - Filter to show only online advisers</li>
              <li><strong>Activity Period</strong> - Filter conversations by time period:
                <ul style={{ marginTop: '5px' }}>
                  <li>All time</li>
                  <li>Today</li>
                  <li>Last 7 days</li>
                  <li>Last 30 days</li>
                </ul>
              </li>
            </List>
            
            <Paragraph>
              After selecting your filters, click "Apply" to update the list. The "Reset" button clears all filters. Active filters will be displayed as a badge on the filter icon and as a summary above the adviser list.
            </Paragraph>
            
            <SectionTitle>Three Dots Menu Options</SectionTitle>
            <Paragraph>
              Clicking the three dots menu for any adviser reveals additional options:
            </Paragraph>
            
            <ContextMenuExample>
              <div className="menu-item">Adviser Information</div>
              <div className="menu-item">Conversation History</div>
            </ContextMenuExample>
            <figcaption style={{ textAlign: 'center', color: '#65748d', fontSize: '12px', marginTop: '5px' }}>
              Three dots menu options
            </figcaption>
            
            <List>
              <li>
                <strong>Adviser Information</strong> - Opens a popup showing detailed information about the adviser including their profile details and status
              </li>
              <li>
                <strong>Conversation History</strong> - Opens a preview of your conversation history with this adviser
              </li>
            </List>
            
            <SectionTitle>Adviser Information Popup</SectionTitle>
            <Paragraph>
              The Adviser Information popup displays detailed information including:
            </Paragraph>
            
            <List>
              <li>Adviser profile image and name</li>
              <li>Online status (online/offline)</li>
              <li>Email and other contact details when available</li>
              <li>Conversation details: start date, message count, etc.</li>
            </List>
            
            <SectionTitle>Conversation History Popup</SectionTitle>
            <Paragraph>
              The Conversation History popup lets you view your message history with an adviser without navigating away from the user list. You can:
            </Paragraph>
            
            <List>
              <li>View all messages organized by date</li>
              <li>See message timestamps</li>
              <li>Export the conversation as a text file</li>
              <li>Open the full conversation view with the "Open Conversation" button</li>
            </List>
            
            <SectionTitle>Online Status Indicators</SectionTitle>
            <Paragraph>
              Each adviser entry displays their current online status:
            </Paragraph>
            
            <List>
              <li><strong>Online</strong> - A green dot indicates the adviser is currently available</li>
              <li><strong>Offline</strong> - A gray dot indicates the adviser is currently unavailable</li>
            </List>
          </>
        );
      
      case 'conversation':
        return (
          <>
            <SectionTitle>Conversation Overview</SectionTitle>
            <Paragraph>
              The Conversation view displays your active chat with a user. You can send and receive messages, 
              share files, and use various features to enhance your communication.
            </Paragraph>
            
            <SectionTitle>Message Input</SectionTitle>
            <Paragraph>
              The message input area at the bottom of the conversation allows you to compose and send messages.
              It offers several features to enhance your communication:
            </Paragraph>
            
            <List>
              <li>
                <strong>Text Input</strong> - Type your message in the input field
              </li>
              <li>
                <strong>Attachments</strong> - Click the attachment icon to upload files like images, documents, and more
              </li>
              <li>
                <strong>Emoji Picker</strong> - Add emotions to your messages by clicking the emoji icon
              </li>
              <li>
                <strong>Voice-to-Text</strong> - Use the microphone icon to record and automatically transcribe your speech directly to the text input
              </li>
              <li>
                <strong>Send Button</strong> - Click the send button or press Enter to send your message
              </li>
            </List>
            
            <FeatureBox>
              <IconBox>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V7a2 2 0 012-2h5.5L17 9.5V19a2 2 0 01-2 2z"></path>
                </svg>
              </IconBox>
              <FeatureContent>
                <h4>Attachment Feature</h4>
                <p>
                  Upload files by clicking the attachment icon. Supported formats include images, documents (PDF, Word, Excel, etc.), 
                  text files, and more. Uploaded files will appear in the conversation and can be downloaded by recipients.
                </p>
              </FeatureContent>
            </FeatureBox>
            
            <FeatureBox>
              <IconBox>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <path d="M8 14s1.5 2 4 2 4-2 4-2"></path>
                  <line x1="9" y1="9" x2="9" y2="9"></line>
                  <line x1="15" y1="9" x2="15" y2="9"></line>
                </svg>
              </IconBox>
              <FeatureContent>
                <h4>Emoji Picker</h4>
                <p>
                  Express yourself with emojis by clicking the emoji icon. The emoji picker provides a wide range of 
                  emoticons, symbols, and expressions organized by categories. Click an emoji to add it to your message.
                </p>
              </FeatureContent>
            </FeatureBox>
            
            <FeatureBox>
              <IconBox>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 2a4 4 0 0 1 4 4v4a4 4 0 0 1-8 0V6a4 4 0 0 1 4-4z"></path>
                  <path d="M19 10v2a7 7 0 0 1-14 0v-2"></path>
                  <line x1="12" y1="19" x2="12" y2="22"></line>
                  <line x1="8" y1="22" x2="16" y2="22"></line>
                </svg>
              </IconBox>
              <FeatureContent>
                <h4>Voice-to-Text</h4>
                <p>
                  When the text input is empty, a microphone icon appears. Click it to start recording your voice.
                  While recording, a timer shows the duration. Click again to stop recording. Your speech will be 
                  automatically transcribed and inserted into the text input area, allowing you to edit it before sending.
                  This saves time compared to typing and makes communication more efficient.
                </p>
              </FeatureContent>
            </FeatureBox>
            
            <SectionTitle>Message History</SectionTitle>
            <Paragraph>
              The main conversation area displays all messages exchanged with the user:
            </Paragraph>
            
            <List>
              <li>
                <strong>Your Messages</strong> - Displayed on the right side with your profile information
              </li>
              <li>
                <strong>User Messages</strong> - Displayed on the left side with their profile information
              </li>
              <li>
                <strong>System Messages</strong> - Important notifications appear in the center
              </li>
              <li>
                <strong>Timestamps</strong> - Each message shows the time it was sent
              </li>
              <li>
                <strong>Attachments</strong> - Files appear as thumbnails or download links
              </li>
            </List>
            
            <SectionTitle>User Status Information</SectionTitle>
            <Paragraph>
              The conversation header shows key information about the user:
            </Paragraph>
            
            <List>
              <li>
                <strong>User Name</strong> - The name of the person you're chatting with
              </li>
              <li>
                <strong>Online Status</strong> - A green dot indicates the user is currently online
              </li>
              <li>
                <strong>Session Time</strong> - When the conversation started
              </li>
            </List>
          </>
        );
      
      // Add more help types as needed
      default:
        return (
          <Paragraph>
            Help content not available for this section.
          </Paragraph>
        );
    }
  };

  return (
    <DialogOverlay>
      <DialogContainer ref={dialogRef}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <CloseButton onClick={onClose}>✕</CloseButton>
        </DialogHeader>
        <DialogContent>
          {renderHelpContent()}
        </DialogContent>
        <Footer>
          <ActionButton onClick={onClose}>Close Help</ActionButton>
        </Footer>
      </DialogContainer>
    </DialogOverlay>
  );
};

export default HelpDialog; 